import { useEffect, useState } from "react";
import {
  Eye,
  EyeOff,
  User,
  Mail,
  Lock,
  Plus,
  X,
  RefreshCw,
  ShieldAlert, // Admin ikonkasini
  Ban, // Bloklash ikonkasini qo'shdik
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { start, done } from "../../store/loaderSlice";
import { snackbarStart } from "../../store/SnackbarSlice";
import axios from "axios";
import { GET_TESTS, REGISTER_URL, GET_USERS } from "../../constant";
import { useTranslation } from "react-i18next";

export default function CreateUser({ setFormStatus, getData, editData }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { token } = useSelector((state) => state.user);
  const [availableRoles, setAvailableRoles] = useState([]);

  // Rejimni aniqlash
  const isEditMode = Boolean(editData);

  const [formData, setFormData] = useState({
    name: "",
    identifier: "",
    password: "",
    roles: [],
    isAdmin: false,
    status: "Active", // Default holat active
  });

  const [showPassword, setShowPassword] = useState(false);
  const [identifierType, setIdentifierType] = useState("email");
  const [selectedRole, setSelectedRole] = useState("");
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  // Agar forma "Edit" rejimida ochilsa, ma'lumotlarni to'ldirish
  useEffect(() => {
    if (isEditMode && editData) {
      setFormData({
        name: editData.name || "",
        identifier: editData.email || "",
        password: "",
        roles: editData.availableTests || [],
        isAdmin: editData.isAdmin || false,
        status: editData.status || "Active",
      });
      setIdentifierType(editData.email?.includes("@") ? "email" : "number");
    } else {
      // Create rejimida bo'shatish
      setFormData({
        name: "",
        identifier: "",
        password: "",
        roles: [],
        isAdmin: false,
        status: "Active",
      });
    }
  }, [editData, isEditMode]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validatsiya
    if (
      !formData.name ||
      !formData.identifier ||
      (!isEditMode && !formData.password) ||
      formData.roles.length === 0
    ) {
      dispatch(
        snackbarStart({
          text: t("createUser.fillRequired"),
          severity: "error",
        }),
      );
      return;
    }

    dispatch(start());

    try {
      const payload = {
        name: formData.name,
        email: formData.identifier,
        availableTests: formData.roles,
        isAdmin: formData.isAdmin,
        status: formData.status, // Blok/Aktiv holatni yuboramiz
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      if (!isEditMode) {
        // Agar yangi yaratilayotgan bo'lsa
        payload.status = "Active";
        payload.isAdmin = false;
      }

      const apiUrl = isEditMode ? `${GET_USERS}/${editData._id}` : REGISTER_URL;
      const apiMethod = isEditMode ? "put" : "post";

      await axios({
        method: apiMethod,
        url: apiUrl,
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          "x-auth-token": token,
        },
        data: payload,
      });

      dispatch(
        snackbarStart({
          text: isEditMode ? t("userEdited") : t("userCreated"),
          severity: "success",
        }),
      );
      setFormStatus(false);
      getData();
      dispatch(done());
    } catch (err) {
      console.log(err);
      dispatch(
        snackbarStart({
          text: err?.response?.data || t("login.smthWentWrong"),
          severity: "error",
        }),
      );
      dispatch(done());
    }
  };

  const handleNameChange = (e) =>
    setFormData({ ...formData, name: e.target.value });
  const handlePasswordChange = (e) =>
    setFormData({ ...formData, password: e.target.value });

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, identifier: value });

    if (value.includes("@")) setIdentifierType("email");
    else if (!isNaN(value) && value !== "") setIdentifierType("number");
    else setIdentifierType("email");
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const addRole = () => {
    if (!selectedRole || formData.roles.includes(selectedRole)) return;
    setFormData({ ...formData, roles: [...formData.roles, selectedRole] });
    setSelectedRole("");
  };

  const removeRole = (roleToRemove) => {
    setFormData({
      ...formData,
      roles: formData.roles.filter((role) => role !== roleToRemove),
    });
  };

  const fetchRoles = () => {
    setIsLoadingRoles(true);
    getTest();
  };

  const getTest = async () => {
    dispatch(start());
    try {
      const { data } = await axios({
        method: "get",
        url: `${GET_TESTS}`,
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
      });
      let mappedRoles = data?.map((test) => ({
        _id: test?._id,
        name: test?.title,
      }));
      setAvailableRoles(mappedRoles);
      setIsLoadingRoles(false);
      dispatch(done());
    } catch (err) {
      console.log(err);
      dispatch(done());
    }
  };

  useEffect(() => {
    getTest();
  }, []);

  return (
    <div className="w-full max-w-md mx-auto bg-white dark:bg-[#0f172a] rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 transition-colors duration-300">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
          {isEditMode
            ? t("createUser.editUser")
            : t("createUser.createNewUser")}
        </h2>
        <button
          onClick={() => setFormStatus(false)}
          className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div>
        <div className="mb-4">
          <label
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5"
            htmlFor="name"
          >
            {t("createUser.fullName")}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <User className="h-5 w-5 text-slate-400" />
            </div>
            <input
              id="name"
              type="text"
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm text-slate-900 dark:text-slate-100 outline-none"
              placeholder={t("createUser.fullNamePlaceholder")}
              value={formData.name}
              onChange={handleNameChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5"
            htmlFor="identifier"
          >
            {t("createUser.identifier")}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              {identifierType === "email" ? (
                <Mail className="h-5 w-5 text-slate-400" />
              ) : (
                <User className="h-5 w-5 text-slate-400" />
              )}
            </div>
            <input
              id="identifier"
              type="text"
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm text-slate-900 dark:text-slate-100 outline-none"
              placeholder={
                identifierType === "email"
                  ? t("createUser.emailPlaceholder")
                  : t("createUser.phonePlaceholder")
              }
              value={formData.identifier}
              onChange={handleIdentifierChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5"
            htmlFor="password"
          >
            {t("createUser.password")}{" "}
            {isEditMode && (
              <span className="font-normal text-xs text-slate-400 dark:text-slate-500">
                {t("createUser.leaveEmpty")}
              </span>
            )}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Lock className="h-5 w-5 text-slate-400" />
            </div>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              className="w-full pl-10 pr-10 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm text-slate-900 dark:text-slate-100 outline-none"
              placeholder={
                isEditMode ? t("createUser.newPasswordPlaceholder") : "••••••••"
              }
              value={formData.password}
              onChange={handlePasswordChange}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors focus:outline-none"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {isEditMode && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t("createUser.accountPermissions")}
              </label>
              <div
                onClick={() =>
                  setFormData({ ...formData, isAdmin: !formData.isAdmin })
                }
                className={`w-full flex items-center justify-between p-3.5 border rounded-xl cursor-pointer transition-all duration-200 ${
                  formData.isAdmin
                    ? "bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30"
                    : "bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
                }`}
              >
                <div className="flex items-center">
                  <ShieldAlert
                    className={`h-5 w-5 mr-3 flex-shrink-0 transition-colors ${formData.isAdmin ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-slate-500"}`}
                  />
                  <div className="flex flex-col">
                    <span
                      className={`text-sm font-semibold transition-colors ${formData.isAdmin ? "text-blue-800 dark:text-blue-300" : "text-slate-700 dark:text-slate-300"}`}
                    >
                      {t("createUser.adminAccess")}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {formData.isAdmin
                        ? t("createUser.adminControl")
                        : t("createUser.restrictedAccess")}
                    </span>
                  </div>
                </div>
                <div
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out ${formData.isAdmin ? "bg-blue-600" : "bg-slate-300 dark:bg-slate-700"}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.isAdmin ? "translate-x-5" : "translate-x-0.5"}`}
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t("createUser.accountStatus")}
              </label>
              <div
                onClick={() =>
                  setFormData({
                    ...formData,
                    status: formData.status === "Active" ? "Blocked" : "Active",
                  })
                }
                className={`w-full flex items-center justify-between p-3.5 border rounded-xl cursor-pointer transition-all duration-200 ${
                  formData.status === "Blocked"
                    ? "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30"
                    : "bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
                }`}
              >
                <div className="flex items-center">
                  <Ban
                    className={`h-5 w-5 mr-3 flex-shrink-0 transition-colors ${formData.status === "Blocked" ? "text-rose-600 dark:text-rose-400" : "text-slate-400 dark:text-slate-500"}`}
                  />
                  <div className="flex flex-col">
                    <span
                      className={`text-sm font-semibold transition-colors ${formData.status === "Blocked" ? "text-rose-800 dark:text-rose-300" : "text-slate-700 dark:text-slate-300"}`}
                    >
                      {formData.status === "Blocked"
                        ? t("createUser.accountBlocked")
                        : t("createUser.accountActive")}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {formData.status === "Blocked"
                        ? t("createUser.cannotLogin")
                        : t("createUser.canLogin")}
                    </span>
                  </div>
                </div>
                <div
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out ${formData.status === "Blocked" ? "bg-rose-500" : "bg-emerald-500"}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.status === "Blocked" ? "translate-x-0.5" : "translate-x-5"}`}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="mb-8">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            {t("createUser.userRoles")}
          </label>
          <div className="flex bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-200">
            <div className="relative flex-1">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full bg-transparent border-none focus:ring-0 appearance-none px-4 py-2.5 text-sm text-slate-900 dark:text-slate-100 outline-none"
                disabled={isLoadingRoles}
              >
                <option value="" className="dark:bg-slate-800">
                  {t("createUser.selectTest")}
                </option>
                {availableRoles.map((role) => (
                  <option
                    key={role._id}
                    value={role._id}
                    className="dark:bg-slate-800"
                  >
                    {role.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            <button
              type="button"
              onClick={addRole}
              className="bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-500/20 px-4 flex items-center justify-center border-l border-slate-200 dark:border-slate-700 transition-colors"
              title={t("createUser.addRole")}
            >
              <Plus className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={fetchRoles}
              className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 px-4 flex items-center justify-center border-l border-slate-200 dark:border-slate-700 transition-colors"
              title={t("createUser.refreshRoles")}
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoadingRoles ? "animate-spin" : ""}`}
              />
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-3 p-2 bg-slate-50 dark:bg-slate-900/50 rounded-lg min-h-[44px] border border-slate-200 dark:border-slate-800 border-dashed">
            {formData.roles.length === 0 && (
              <span className="text-sm text-slate-400 dark:text-slate-500 italic py-1 px-2">
                {t("createUser.noRoles")}
              </span>
            )}
            {formData.roles.map((role) => {
              const roleObj = availableRoles.find((r) => r._id === role);
              const displayName = roleObj ? roleObj.name : role;

              return (
                <div
                  key={role}
                  className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1 rounded-full flex items-center text-sm shadow-sm"
                >
                  <span className="text-slate-700 dark:text-slate-300 text-xs font-medium">
                    {displayName}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRole(role)}
                    className="ml-2 text-slate-400 hover:text-rose-500 dark:hover:text-rose-400 focus:outline-none"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex gap-4 pt-2 border-t border-slate-200 dark:border-slate-800 mt-6">
          <button
            type="button"
            onClick={() => setFormStatus(false)}
            className="flex-1 px-4 py-2.5 bg-white dark:bg-[#0f172a] border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold transition-colors duration-200 text-sm shadow-sm"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-[2] bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-2.5 px-4 rounded-lg font-semibold transition-all duration-200 text-center shadow-md hover:shadow-lg shadow-blue-500/20 text-sm"
          >
            {isEditMode
              ? t("createUser.updateUserBtn")
              : t("createUser.createUserBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
